import { ValueOf } from 'type-fest';

import {
  type PartBotLocation,
  type TableExtractionPayload,
  type ExtractedTableJson,
  TextSnippet,
  PartBotResultJson,
  FieldSuggestion,
} from '@frontend/api';

import { type ISuggestionAnnotation } from './SuggestionAnnotation';
import {
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  SUGGESTION_ANNOTATION_TYPES,
  SUGGESTION_ANNOTATION_COMMAND_ORIGINS,
} from './consts';

export type Geometry = {
  x1: number; // relative to left of page
  y1: number; // relative to top of page
  width: number; // left to right
  height: number; // top to bottom
};

export const ANNOTATION_METADATA_TYPES = {
  EXTRACTED_TABLE: 'extractedTable',
  FOUND_IN_FILES: 'foundInFiles',
  FIELD_SUGGESTION: 'fieldSuggestion',
  ACTION_EXTRACTION_TEXT: 'actionExtractionText',
} as const;

export type FoundInFileMetadata = {
  type: typeof ANNOTATION_METADATA_TYPES.FOUND_IN_FILES;
  partBotResult: PartBotResultJson;
};

export type FieldSuggestionMetadata = {
  type: typeof ANNOTATION_METADATA_TYPES.FIELD_SUGGESTION;
  fieldSuggestion: FieldSuggestion;
};

export type ExtractedTableMetadata = {
  type: typeof ANNOTATION_METADATA_TYPES.EXTRACTED_TABLE;
  tableJson: ExtractedTableJson;
};

export type ActionExtractionTextMetadata = {
  type: typeof ANNOTATION_METADATA_TYPES.ACTION_EXTRACTION_TEXT;
  textPositionResult: TextPositionResult;
};

export type SuggestionAnnotationMetadata =
  | ExtractedTableMetadata
  | FoundInFileMetadata
  | FieldSuggestionMetadata
  | ActionExtractionTextMetadata;

export type SuggestionAnnotationRegionState = {
  label?: string;
  outlineOpacity: number;
  outlineWidth: number;
  patternOpacity: number;
};

/**
 * Display type for suggestion annotations.
 * - 'content': Standard text/element highlight with solid fill (default)
 * - 'region': Larger area highlight with diagonal stripe pattern and feathered edges
 */
export type SuggestionAnnotationDisplayType = 'content' | 'region';

export type SuggestionAnnotationRaw = {
  type: ValueOf<typeof SUGGESTION_ANNOTATION_TYPES>;
  content: string;
  partFileId: string;
  id: string;
  page: number | null;
  // We either get loc from the serializer, or geometry built in
  loc?: PartBotLocation | null;
  geometry?: Geometry | null;
  category: string; // used to dictate whiteout category control and groupings
  colorCode: string; // used to dictate annotation color
  // for standard "content" annotations
  opacity: number;
  borderWidth: number;
  metadata?: SuggestionAnnotationMetadata;
  /**
   * Display type determines the visual rendering style:
   * - 'content': Standard solid fill with border (default)
   * - 'region': Diagonal stripe pattern with feathered edges, larger radius
   */
  displayType?: SuggestionAnnotationDisplayType;
  defaultRegionState?: SuggestionAnnotationRegionState;
};

type CommandPayload = {
  [SUGGESTION_ANNOTATION_COMMAND_TYPES.REFRESH_EXTRACTED_TABLES]: TableExtractionPayload;
};

export type ActionTextSnippet = TextSnippet & {
  actionUuid: string;
  keyWithoutGeometry: string;
};

export type TextPositionGeometry = {
  x: number;
  y: number;
  width: number;
  height: number;
  keyWithGeometry: string;
  loc: PartBotLocation;
};

export type TextPositionResult = {
  snippet: ActionTextSnippet;
  geometries: TextPositionGeometry[];
};

export type SuggestionAnnotationState = {
  isHidden: boolean;
  isFocused: boolean;
  isWhiteout: boolean;
  isDimmed: boolean;
  colorCode: string;
  opacity: number;
  borderWidth: number;
  isBleedingOrange: boolean;
  displayType: SuggestionAnnotationDisplayType;
  regionState?: SuggestionAnnotationRegionState;
};

export type SuggestionAnnotationStateMap = Record<
  SuggestionAnnotationRaw['id'],
  SuggestionAnnotationState
>;

export type SuggestionAnnotationStateUpdate = Record<
  SuggestionAnnotationRaw['id'],
  Partial<SuggestionAnnotationState>
>;

export type SuggestionAnnotationCommandNoId = {
  type: ValueOf<typeof SUGGESTION_ANNOTATION_COMMAND_TYPES>;
  origin?: ValueOf<typeof SUGGESTION_ANNOTATION_COMMAND_ORIGINS>;
  annotations?: SuggestionAnnotationRaw[] | null;
  annotationIds?: string[] | null;
  predicate?: (viewerAnnotation: ISuggestionAnnotation) => boolean | null;
  mergeState?: SuggestionAnnotationStateUpdate;
  actionTextSnippets?: ActionTextSnippet[];
  textExtractions?: TextPositionResult[];
  payload?: CommandPayload[keyof CommandPayload];
};

export type SuggestionAnnotationCommand = SuggestionAnnotationCommandNoId & {
  id: string;
};
